import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchCompleteGame } from '../../api/gameDetail';
import { parseBoxScore } from '../../api/pitchByPitch';
import BoxScoreTable from '../gameCenter/BoxScoreTable';
import PlayerProfile from '../PlayerProfile';
import RecapInningBreakdown from '../recap/RecapInningBreakdown';
import RecapTimeline from '../recap/RecapTimeline';
import RecapTopPerformersClickable from './RecapTopPerformersClickable';
import RecapDecisionsClickable from './RecapDecisionsClickable';
import GameNewsPanel from './GameNewsPanel';

const {
  FiArrowLeft,
  FiCalendar,
  FiMapPin,
  FiRefreshCw,
  FiShare2
} = FiIcons;

const TABS = [
  'Summary',
  'Box Score',
  'Innings',
  'Timeline',
  'Performers',
  'News & Videos'
];

const formatGameDate = (game) => {
  const value = game?.officialDate || String(game?.date || '').slice(0, 10);
  if (!value) return '--';

  try {
    return format(parseISO(value), 'EEEE, MMMM d, yyyy');
  } catch {
    return value;
  }
};

const GameDetailPage = () => {
  const { slug, gamePk } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('Summary');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [copied, setCopied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await fetchCompleteGame({
          gamePk,
          slug,
          signal: controller.signal
        });

        setGame(result.game);
        setContent(result.content);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setGame(null);
          setContent(null);
          setError(
            requestError.message ||
            'This game could not be loaded from MLB.'
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [gamePk, slug, reloadKey]);

  useEffect(() => {
    if (!game) return;

    const away = game.away?.abbr || 'Away';
    const home = game.home?.abbr || 'Home';
    document.title = `${away} ${game.away?.score} - ${game.home?.score} ${home} | Green Monster`;
  }, [game]);

  const boxData = useMemo(
    () => parseBoxScore(game?.boxScore),
    [game?.boxScore]
  );

  const feedLike = useMemo(() => {
    if (!game) return null;
    return game.feed || {
      liveData: {
        boxscore: game.boxScore,
        decisions: game.decisions
      }
    };
  }, [game]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1e3a29]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#658d71] border-t-[#d8ceb3]" />
          <span className="text-xs uppercase tracking-widest text-[#a9c9b1]">
            Loading game, box score, and highlights...
          </span>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#1e3a29] p-4">
        <div className="flex max-w-md flex-col items-center gap-4 border-2 border-[#658d71] p-8 text-center">
          <p className="text-sm font-bold uppercase tracking-widest text-[#d8ceb3]">
            Game details unavailable
          </p>
          <p className="text-xs leading-relaxed text-[#a9c9b1]">
            {error || 'MLB could not find this game.'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setReloadKey((value) => value + 1)}
              className="flex items-center gap-1.5 border border-[#d8ceb3] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[#d8ceb3]"
            >
              <SafeIcon icon={FiRefreshCw} size={11} />
              Retry
            </button>
            <button
              type="button"
              onClick={() => navigate('/history')}
              className="border border-[#658d71] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[#a9c9b1]"
            >
              Game History
            </button>
          </div>
        </div>
      </div>
    );
  }

  const awayWon = Number(game.away?.score) > Number(game.home?.score);
  const homeWon = Number(game.home?.score) > Number(game.away?.score);

  return (
    <div className="min-h-screen bg-[#1e3a29] p-3 sm:p-6 md:p-10">
      <main className="mx-auto flex max-w-5xl flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate('/history')}
            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#a9c9b1] hover:text-white"
          >
            <SafeIcon icon={FiArrowLeft} size={14} />
            Game History
          </button>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setReloadKey((value) => value + 1)}
              className="border border-[#658d71] p-2 text-[#a9c9b1] hover:text-white"
              aria-label="Refresh game details"
            >
              <SafeIcon icon={FiRefreshCw} size={12} />
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-1.5 border border-[#658d71] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[#a9c9b1] hover:text-white"
            >
              <SafeIcon icon={FiShare2} size={11} />
              {copied ? 'Copied' : 'Share'}
            </button>
          </div>
        </div>

        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.15)] p-5 sm:p-8"
        >
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <SafeIcon icon={FiCalendar} size={12} className="text-[#658d71]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#658d71]">
              {formatGameDate(game)}
            </span>
            <span className="text-[#658d71]">·</span>
            <SafeIcon icon={FiMapPin} size={12} className="text-[#658d71]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#658d71]">
              {game.venue || '--'}
            </span>
          </div>

          <div className="flex items-center gap-4 sm:gap-8">
            <div className="flex flex-1 flex-col items-start">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#658d71]">
                Away
              </span>
              <span className={`text-base font-black uppercase ${awayWon ? 'text-[#d8ceb3]' : 'text-[#a9c9b1]'}`}>
                {game.away?.name || game.away?.abbr}
              </span>
              <span className={`mt-1 text-5xl font-black tabular-nums sm:text-6xl ${awayWon ? 'text-white' : 'text-[#658d71]'}`}>
                {game.away?.score ?? '--'}
              </span>
            </div>

            <span className="text-xs font-black uppercase tracking-widest text-[#658d71]">
              {game.status || 'Final'}
            </span>

            <div className="flex flex-1 flex-col items-end text-right">
              <span className="text-[9px] font-bold uppercase tracking-widest text-[#658d71]">
                Home
              </span>
              <span className={`text-base font-black uppercase ${homeWon ? 'text-[#d8ceb3]' : 'text-[#a9c9b1]'}`}>
                {game.home?.name || game.home?.abbr}
              </span>
              <span className={`mt-1 text-5xl font-black tabular-nums sm:text-6xl ${homeWon ? 'text-white' : 'text-[#658d71]'}`}>
                {game.home?.score ?? '--'}
              </span>
            </div>
          </div>
        </motion.section>

        <div className="flex overflow-x-auto border-b-2 border-[#658d71]">
          {TABS.map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => setTab(value)}
              className={`whitespace-nowrap border-r border-[#658d71] px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest ${
                tab === value
                  ? 'bg-[#386646] text-[#d8ceb3]'
                  : 'text-[#a9c9b1] hover:text-white'
              }`}
            >
              {value}
            </button>
          ))}
        </div>

        <motion.section
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4"
        >
          {tab === 'Summary' && (
            <RecapDecisionsClickable
              game={{
                decisions: game.decisions,
                teams: {
                  away: { team: { abbreviation: game.away?.abbr } },
                  home: { team: { abbreviation: game.home?.abbr } }
                }
              }}
              feed={feedLike}
              onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
            />
          )}

          {tab === 'Box Score' && (
            <BoxScoreTable
              boxData={boxData}
              awayAbbr={game.away?.abbr || 'AWY'}
              homeAbbr={game.home?.abbr || 'HME'}
              onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
            />
          )}

          {tab === 'Innings' && (
            <RecapInningBreakdown
              innings={game.innings}
              awayAbbr={game.away?.abbr}
              homeAbbr={game.home?.abbr}
            />
          )}

          {tab === 'Timeline' && (
            <RecapTimeline allPlays={game.allPlays} />
          )}

          {tab === 'Performers' && (
            <RecapTopPerformersClickable
              feed={feedLike}
              onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
            />
          )}

          {tab === 'News & Videos' && (
            <GameNewsPanel content={content} />
          )}
        </motion.section>
      </main>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.id}
          playerName={selectedPlayer.name}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </div>
  );
};

export default GameDetailPage;